/**
 * Client-side anonymity-mining claim orchestration.
 *
 * Mirrors `withdrawFlow.ts` for the AM circuit. The note holder
 * generates a zero-knowledge proof that ties their deposit's commitment
 * to an AM nullifier hash without revealing which leaf they own. The
 * AM nullifier is domain-separated (Poseidon(2)(nullifier, AM_DOMAIN_TAG))
 * from the withdraw nullifier (Poseidon(1)(nullifier)), so claiming AM
 * does NOT consume the right to later withdraw the same deposit.
 *
 * The proof can be self-submitted (via `buildAmClaimMessageBody`) or
 * relayed (via `buildAmClaimBroadcastPayload`, sent to a relayer that
 * exposes the matching POST /claim-am endpoint).
 */
import { Address, toNano } from "@ton/core";
import axios from "axios";
import { parseNote } from "./notes";
import { addressToFieldElement } from "./tonClient";
import { buildPoseidon } from "./poseidon";
import { buildMerkleTree, merkleProofForLeaf } from "./merkleTree";
import { generateAmClaimProof } from "./proof";
import { TonadoPool } from "./pool";
import { AM_DOMAIN_TAG } from "./amDistributor";
import { scanPoolTransactions } from "./indexer";
import { getPool } from "./deployments";
/** Compute the AM nullifier hash via Poseidon(2)(nullifier, AM_DOMAIN_TAG). */
export async function computeAmNullifier(nullifier) {
    const poseidon = await buildPoseidon();
    return poseidon([nullifier, AM_DOMAIN_TAG]);
}
/**
 * Build an AM claim proof entirely client-side. The output is suitable
 * for either self-submission to the AmDistributor or relayer broadcast.
 *
 * Public-signal order MUST match amClaim.circom :: main:
 *   [root, amNullifierHash, recipient, relayer, fee, refund]
 */
export async function buildAmClaimArgs(opts) {
    const parsed = await parseNote(opts.note);
    const network = opts.network ?? parsed.network;
    const pool = getPool(network, parsed.assetId, parsed.amount);
    if (!pool || !pool.address) {
        throw new Error(`No deployed pool for ${parsed.assetId}-${parsed.amount} on ${network}. ` +
            `AM claims require the deposit to exist in a deployed pool.`);
    }
    let commitments;
    if (opts.preloadedCommitments) {
        commitments = opts.preloadedCommitments;
    }
    else if (opts.client) {
        commitments = await loadCommitmentsFromChain(opts.client, pool.address);
    }
    else {
        throw new Error("Either pass `client` or `preloadedCommitments` — no source for the merkle leaves was provided.");
    }
    const tree = await buildMerkleTree({ leaves: commitments });
    const merkle = await merkleProofForLeaf(tree, parsed.deposit.commitment);
    // Optional fail-fast: same shape as withdrawFlow — when a TonClient
    // is available, verify the root on-chain before paying the prover.
    // Browser callers skip this; ERR_UNKNOWN_ROOT from the distributor
    // catches the stale-root case at claim time.
    if (opts.client) {
        const onchain = TonadoPool.at(Address.parse(pool.address));
        const provider = opts.client.provider(onchain.address);
        const known = await onchain.isKnownRoot(provider, merkle.root);
        if (!known) {
            throw new Error(`Computed merkle root not in on-chain known-roots set. Re-sync your local ` +
                `commitments before claiming AM.`);
        }
    }
    const amNullifierHash = await computeAmNullifier(parsed.deposit.nullifier);
    const { proof, publicSignals } = await generateAmClaimProof({
        root: merkle.root,
        amNullifierHash,
        recipient: addressToFieldElement(opts.recipient),
        relayer: addressToFieldElement(opts.relayer),
        fee: opts.fee,
        refund: opts.refund ?? 0n,
        nullifier: parsed.deposit.nullifier,
        secret: parsed.deposit.secret,
        pathElements: merkle.pathElements,
        pathIndices: merkle.pathIndices,
    });
    return {
        proof,
        publicSignals,
        root: merkle.root,
        amNullifierHash,
        recipient: opts.recipient,
        relayer: opts.relayer,
        fee: opts.fee,
        refund: opts.refund ?? 0n,
    };
}
export function buildAmClaimBroadcastPayload(args) {
    const a = args.args;
    return {
        distributor: args.distributor,
        proof: a.proof,
        publicSignals: a.publicSignals.slice(0, 6),
        recipient: a.recipient.toString(),
        fee: a.fee.toString(),
        refund: a.refund.toString(),
    };
}
export const DEFAULT_AM_CLAIM_VALUE = toNano("0.4");
async function loadCommitmentsFromChain(client, poolAddress) {
    const collected = [];
    let cursor = {};
    while (true) {
        const { events, lastLt, lastHash } = await scanPoolTransactions(client, {
            pool: Address.parse(poolAddress),
            maxTransactions: 256,
            fromLt: cursor.lt,
            fromHash: cursor.hash,
        });
        for (const e of events) {
            collected.push({ commitment: e.commitment, leafIndex: e.leafIndex });
        }
        if (!lastLt || (cursor.lt !== undefined && lastLt === cursor.lt))
            break;
        cursor = { lt: lastLt, hash: lastHash };
    }
    collected.sort((a, b) => a.leafIndex - b.leafIndex);
    return collected.map((e) => e.commitment);
}
/** Optional helper: pull commitments from a relayer's tree endpoint.
 *  Mirrors withdrawFlow.ts :: loadCommitmentsFromRelayer. */
export async function loadAmCommitmentsFromRelayer(relayerUrl, poolAddress) {
    const out = [];
    let from = 0;
    while (true) {
        const resp = await axios.get(`${relayerUrl}/tree/commitments`, {
            params: { pool: poolAddress, from, limit: 5000 },
            timeout: 60_000,
        });
        for (const c of resp.data.commitments)
            out.push(BigInt(c.commitment));
        if (resp.data.nextFrom == null)
            break;
        from = resp.data.nextFrom;
    }
    return out;
}
//# sourceMappingURL=amClaimFlow.js.map